<template>
  <OmtLayoutHeaderFooter
    :footer="false"
    @profilePicClick="showImageCropper = true"
  >
    <OmtGrid
      px="0 !important"
      my="0 !important"
    >
      <ImageCropper
        v-if="showImageCropper"
        @hideImageCropper="hideImagePicker"
        @cropped="onImageResult"
      />

      <OmtRow
        :desktopStart="1"
        :desktopEnd="5"
        :mb="['24px', '0']"
      >
        <CBox>
          <CFlex
            align-items="center"
            mb="24px"
            :display="['none', 'flex']"
          >
            <Profile
              :profilePic="profile.profilePic"
              @showPicker="onShowImagePicker"
            />
            <OmtTypography
              ml="16px"
              as="h1-large"
              color="var(--colors-light-primary-navy-blue)"
            >
              {{ profileName }}
            </OmtTypography>
          </CFlex>
          <template>
            <CFlex
              v-if="messagesToDisplay.length > 0"
              :display="['block', 'none']"
            >
              <OmtSwiper
                v-if="messagesToDisplay.length > 0"
                key="messagesSwiper"
                :autoplay="5000"
                data-cy="home-notice-swiper"
                :ml="['16px', '0']"
                :class="
                  notificationSelected > 0 && notificationSelected === messagesToDisplay.length - 1
                    ? 'margin-finish-carousel'
                    : ''
                "
              >
                <OmtSwiperSlide
                  v-for="(item, id) in messagesToDisplay"
                  :key="id"
                  maxWidth="322px"
                  :slidesPerView="1.1"
                >
                  <HomeNoticeSwiperCard
                    v-chakra
                    :item="item"
                    maxWidth="322px"
                    minWidth="322px"
                  />
                </OmtSwiperSlide>
              </OmtSwiper>
            </CFlex>
            <CFlex
              v-if="profileNotificationChunks.length > 0"
              :display="['none', 'block']"
              mb="32px"
            >
              <OmtSwiper
                key="notificationSwiper"
                :autoplay="5000"
                :slidesPerView="1"
                :spaceBetween="16"
                :navigation="true"
                :goToSlide="Number(notificationSelected)"
                mb="16px"
              >
                <OmtSwiperSlide
                  v-for="(chunks, i) in profileNotificationChunks"
                  :key="i"
                >
                  <HomeNoticeSwiperCard
                    v-for="(item, id) in chunks"
                    :key="id"
                    v-chakra
                    :item="item"
                    :mb="id === 0 && '16px'"
                  />
                </OmtSwiperSlide>
              </OmtSwiper>
            </CFlex>
          </template>
        </CBox>
      </OmtRow>

      <OmtRow
        :desktopStart="7"
        :desktopEnd="12"
        mb="32px"
        justify-content="flex-end"
      >
        <CBox
          v-if="credentials.length > 0"
          :maxWidth="['100%', '390px']"
        >
          <OmtTypography
            as="p1"
            :display="['none', 'block']"
          >
            Visualizar informações da credencial do beneficiário:
          </OmtTypography>

          <CBox :p="['0 16px', '0']">
            <OmtSelect
              :initialValue="selectedCredencialName"
              mb="24px"
              data-cy="home-credential-select"
              @change="onCredentialChange($event)"
            >
              <option
                v-for="cred in credentialNames"
                :key="cred.index"
                :value="cred.index"
              >
                {{ formatCredentialName(cred.name) }}
                {{ cred.titular ? "- Titular" : "- Dependente" }}
              </option>
            </OmtSelect>
          </CBox>
          <OmtSwiper
            v-chakra
            class="CardSwiper"
            mb="16px"
            refs="credentialSwiper"
            data-cy="home-credential-swiper"
            :goToSlide="Number(credentialSelected)"
            :ml="['16px', '0']"
            :class="
              credentialSelected > 0 && credentialSelected === credentials.length - 1 ? 'margin-finish-carousel' : ''
            "
            :breakPoints="{
              320: {
                slidesPerView: 1.1
              },
              1024: {
                slidesPerView: 1
              }
            }"
            @slide-atual="onCredentialChange($event)"
          >
            <OmtSwiperSlide
              v-for="(item, id) in credentials"
              :key="id"
            >
              <HomeCredential
                v-chakra
                data-cy="home-credential"
                :credential="item"
              />
            </OmtSwiperSlide>
          </OmtSwiper>

          <CBox
            w="100%"
            d="inline-flex"
            min-height="46px"
          >
            <CFlex
              d="inline-flex"
              w="100%"
              alignItems="center"
              :justifyContent="['center', 'flex-start']"
            >
              <CFlex
                :display="['none', 'block']"
                :justifyContent="['', 'space-between']"
              >
                <OmtSwiperNavigation
                  :disabledPrev="credentialSelected === 0"
                  :disabledNext="credentialSelected === credentials.length - 1"
                />
              </CFlex>
              <OmtButton
                v-if="displayShareButton"
                :width="['45%', '35%']"
                ml="2.5%"
                mr="2.5%"
                height="46px"
                variant="outline"
                border="1px solid var(--colors-light-secondary-divider)"
                @click="irParaCredencial(credentialSelected)"
              >
                <OmtTypography as="h3-bold">Compartilhar</OmtTypography>
              </OmtButton>
              <OmtButton
                v-if="credentials[credentialSelected] && credentials[credentialSelected].isMdSv !== true"
                :width="['45%', '']"
                :ml="displayShareButton ? 'unset' : '2.5%'"
                :mr="['2%', '0']"
                height="46px"
                variant="outline"
                border="1px solid var(--colors-light-secondary-divider)"
                d="flex"
                alignItems="center"
                justifyContent="center"
              >
                <OmtTypography as="h3-bold">Token:</OmtTypography>
                <OmtTypography
                  as="button-large"
                  ml="7px"
                  letterSpacing="7px"
                  mr="-7px"
                >
                  {{ createToken(credentialSelected) }}
                </OmtTypography>
              </OmtButton>
            </CFlex>
          </CBox>
        </CBox>
      </OmtRow>

      <OmtRow
        v-if="displayShortcuts.length > 0"
        mb="40px"
        py="24px"
        flex-direction="column"
        class="shortcuts-section"
      >
        <CFlex
          justify-content="space-between"
          align-items="baseline"
          margin-bottom="16px"
        >
          <CBox :p="['0 16px', '0']">
            <OmtTypography as="h1-large">Meus atalhos</OmtTypography>
          </CBox>

          <CBox
            align="end"
            :mr="['16px', '0']"
            mb="14px"
            data-cy="home-edit-shortcuts"
          >
            <router-link :to="{ path: '/edit-shortcuts' }">
              <CBox :d="['none', 'block']"
                ><OmtIcons
                  cursor="pointer"
                  name="pen-to-square"
                  size="xl"
              /></CBox>
              <CBox :d="['block', 'none']"
                ><OmtIcons
                  cursor="pointer"
                  name="pen-to-square"
              /></CBox>
            </router-link>
          </CBox>
        </CFlex>

        <CBox class="shortcuts-swiper-wrap">
          <OmtSwiper class="AtalhosSwiper">
            <OmtSwiperSlide
              v-for="(item, id) in displayShortcuts"
              :key="item.View || item.Label || id"
              :ml="id === 0 && ['16px', '0']"
              w="90px !important"
              data-cy="home-shortcuts-card"
              :class="id > 0 && id === displayShortcuts.length - 1 ? 'margin-finish-carousel' : ''"
              cursor="pointer"
              @click.prevent="quickMenuOnClick(item.View)"
            >
              <div
                class="shortcut-item"
                v-bind="isBuscaRedeShortcut(item) ? { 'data-tour': 'busca-rede-atalho' } : {}"
              >
                <span
                  v-if="item.isNew"
                  class="shortcut-new-badge"
                  >NOVO</span
                >
                <OmtCard
                  class="shortcut-card"
                  :class="{ 'shortcut-card--hub': isHubSaudeShortcut(item) }"
                  padding="24px"
                  h="100%"
                  w="100%"
                  display="flex"
                  justify-content="center"
                  align-items="center"
                  position="relative"
                  overflow="visible"
                >
                  <OmtIcons
                    :name="item.Icon"
                    size="lg"
                    :color="isHubSaudeShortcut(item) ? 'white' : undefined"
                  />
                </OmtCard>
              </div>
              <OmtTypography
                as="button-small"
                :width="['fit-content', '100%']"
                mt="8px !important"
                text-align="center"
                display="flex"
                align-items="center"
                justify-content="center"
                w="100%"
              >
                {{ item.Label }}
              </OmtTypography>
            </OmtSwiperSlide>
          </OmtSwiper>
        </CBox>
      </OmtRow>

      <OmtRow
        v-if="hasActivities"
        mb="14px"
        :p="['0 16px', '0']"
      >
        <OmtTypography as="h1-large">Minhas atividades</OmtTypography>
      </OmtRow>

      <OmtRow :mb="hasActivities ? '36px' : ''">
        <TaskList />
      </OmtRow>

      <OmtRow
        mb="14px"
        :p="['0 16px', '0']"
      >
        <OmtTypography as="h1-large">Destaques Omint</OmtTypography>
      </OmtRow>

      <OmtRow
        v-if="blogPosts.length > 0"
        mb="42px"
      >
        <HomeBlogPosts
          data-cy="home-blog-swiper"
          :posts="blogPosts"
        />
      </OmtRow>
    </OmtGrid>

    <Onboard
      v-if="shouldOpenOnboard"
      onboard="initial-onboard"
    />
    <CheckupDigitalInviteModal :isOpen.sync="displayCheckupInvite" />
    <template v-if="displayModalMessage">
      <HomeNoticeModal
        :isOpen.sync="displayModalMessage"
        :title="modalMessage.Mensagem"
        :text="modalMessage.MensagemPopUp"
        @close="closeModalMessage"
      />
    </template>
    <div v-show="false">
      <div
        v-if="protocoloAvalicaoApp"
        ref="avaliacaoApp"
      >
        <RatingForm
          :protocolo="protocoloAvalicaoApp"
          tipo="601"
          etapa="P"
        />
      </div>
    </div>
  </OmtLayoutHeaderFooter>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from "vuex";
import Credential from "@/components/Organisms/Credential";
import ImageCropper from "@/components/Molecules/ImageCropper";
import Profile from "@/components/Molecules/Profile";
import TaskList from "@/components/Organisms/TaskList";
import RatingForm from "@/components/Organisms/RatingForm";
import HomeCredential from "@/components/Organisms/Home/HomeCredential";
import HomeBlogPosts from "@/components/Organisms/Home/HomeBlogPosts";
import HomeNoticeSwiperCard from "@/components/Organisms/Home/HomeNoticeSwiperCard";
import HomeNoticeModal from "@/components/Organisms/Home/HomeNoticeModal.vue";
import CheckupDigitalInviteModal from "@/components/Organisms/CheckupDigitalOmint/InviteModal.vue";
import OmintOTP from "@/utils/otpToken";
import { canSeeCheckupDigitalOmint, wasCheckupInviteDismissed } from "@/utils/checkupDigitalOmint";
import { getBoaHoraHomeTask } from "@/utils/programaBoaHora";
import { BENEFICIO_FARMACIA_ROUTE, PLANO_MEDICAMENTOS_NOTICE_INDEX } from "@/utils/beneficioFarmacia";
import Onboard, { onboards } from "@/components/Organisms/Onboard";
import { downloadUrl } from "@/utils/downloadUtil";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import { PERFIL_ASSOCIADO_KIPP } from "@/utils/perfilRouteUtil";
import { isMobile } from "@/utils/utils";
import { maybeStartBuscaRedeTourFromHome } from "@/utils/buscaRedeTour";
import { isHubSaudeItem } from "@/utils/hubSaude";
import _ from "lodash";

const otp = new OmintOTP();

function formatPersonName(name) {
  if (!name) return "";
  return String(name)
    .toLowerCase()
    .replace(/\b\w/g, match => match.toUpperCase());
}

export default {
  name: "Home",
  components: {
    Profile,
    Credential,
    ImageCropper,
    TaskList,
    RatingForm,
    HomeCredential,
    HomeNoticeSwiperCard,
    HomeNoticeModal,
    CheckupDigitalInviteModal,
    HomeBlogPosts,
    Onboard
  },
  data() {
    return {
      displayCheckupInvite: false,
      displayModalMessage: false,
      modalMessage: {},
      showImageCropper: false,
      credentialSelected: 0,
      notificationSelected: 0,
      initialOnboardOptin: false
    };
  },
  computed: {
    ...mapState("tasks", ["taskList"]),
    ...mapState("myAccount", ["blogPosts"]),
    ...mapState("biometrics", ["hasBiometric", "shoudUseBiometric", "listenersRegistred"]),
    ...mapState("saude", [
      "profile",
      "perfil",
      "credentials",
      "profileName",
      "vinculo",
      "protocoloAvalicaoApp",
      "menusFavoritos",
      "menu"
    ]),
    ...mapGetters("saude", ["baseUrl", "defaultRoute", "onboardWasAlreadyClosed"]),
    displayShareButton() {
      return this.perfil !== PERFIL_ASSOCIADO_KIPP;
    },
    isMobile() {
      return isMobile();
    },
    initialOnboardWasAlreadyClosed() {
      return this.onboardWasAlreadyClosed("initial-onboard");
    },
    shouldOpenOnboard() {
      return this.isMobile && this.initialOnboardOptin && !this.initialOnboardWasAlreadyClosed;
    },
    messagesToDisplay() {
      let messages = [];
      if (this.profile?.messages?.length) {
        messages = this.profile.messages.filter(
          m =>
            !!m.Link &&
            (m.Alerta === "S" || m.Alerta === "N") &&
            m.index !== PLANO_MEDICAMENTOS_NOTICE_INDEX &&
            m.Link !== BENEFICIO_FARMACIA_ROUTE
        );
        if (!(this.hasBiometric && this.listenersRegistred && !this.shoudUseBiometric)) {
          messages = messages.filter(item => !(item.TipoAviso === 7 && item.Link === "/settings"));
        }
      }
      return this.withBoaHoraNotice(messages);
    },
    credentialNames() {
      if (!this.credentials?.length) return [];
      const names = [];
      this.credentials.forEach((c, index) => {
        if (!names.some(n => n.name === c.name)) {
          names.push({
            index: `${index}`,
            name: c.name,
            displayName: formatPersonName(c.name),
            number: c.number,
            titular: c.titular
          });
        }
      });
      return names;
    },
    selectedCredencialName() {
      if (!this.credentials?.length) return 0;
      if (this.credentialSelected < 0) return 0;
      if (this.credentialSelected >= this.credentials.length) return 0;
      const names = this.credentialNames.filter(c => c.index <= this.credentialSelected);
      return `${names?.length ? names[names.length - 1]?.index ?? 0 : 0}`;
    },
    profileNotificationChunks() {
      return _.chunk(this.messagesToDisplay, 2);
    },
    tokenValue() {
      return this.createToken(this.credentialSelected);
    },
    hasActivities() {
      return this.taskList.length > 0 || !!getBoaHoraHomeTask(this.vinculo);
    },
    displayShortcuts() {
      // Os atalhos vem do banco (ObterMenusFavoritos): o front nao acrescenta nenhum.
      return this.menusFavoritos || [];
    }
  },
  watch: {
    vinculo: {
      immediate: true,
      handler() {
        this.maybeOpenCheckupInvite();
      }
    }
  },
  async created() {
    this.registerListeners();

    try {
      if (this.initialOnboardWasAlreadyClosed) return;
      const pendingOptin = await this.getOnboardOptin({ template: onboards["initial-onboard"].template });

      this.initialOnboardOptin = !!pendingOptin?.Texto;
    } catch (error) {
      console.error(error);
    }
  },
  async mounted() {
    if (!this.isCorrectHome()) return;
    try {
      await this.LoadTokenData();
      await this.SyncToken();
    } finally {
      this.$root.$emit("hide-loading");
    }
    this.checkForBiometric();
    this.checkForNativeShare();
    this.checkPermissionForResource("camera");
    this.checkPermissionForResource("microfone");
    this.checkPermissionForResource("location");
    this.credentialSelected = 0;
    if (!this.blogPosts?.length) {
      this.fetchBlogPosts();
    }
    this.displayAlerts();
    this.maybeOpenCheckupInvite();
    this.$nextTick(() => {
      maybeStartBuscaRedeTourFromHome(this);
    });
  },
  methods: {
    ...mapActions("myAccount", ["fetchBlogPosts"]),
    ...mapMutations("saude", ["CLEAR_PROTOCOLO_AVALIACAO_APP"]),
    ...mapActions("saude", [
      "hydrate",
      "fetchProfileImage",
      "changeProfilePick",
      "getOnboardOptin",
      "registerAlertNotification"
    ]),
    ...mapActions("pushToken", ["LoadTokenData", "SyncToken"]),
    ...mapActions("biometrics", [
      "checkForBiometric",
      "registerListeners",
      "checkForNativeShare",
      "checkPermissionForResource"
    ]),
    changeCredentialCarousel(action) {
      if (action === "next" && this.credentialSelected < this.credentials.length - 1) {
        this.credentialSelected += 1;
      }
      if (action === "prev" && this.credentialSelected > 0) {
        this.credentialSelected -= 1;
      }
    },
    onCredentialChange(e) {
      this.credentialSelected = Number(e);
    },
    formatCredentialName(name) {
      if (!name) return "";
      return name.toLowerCase().replace(/\b\w/g, match => match.toUpperCase());
    },
    withBoaHoraNotice(messages) {
      if (!canSeeCheckupDigitalOmint(this.vinculo)) return messages;
      return [
        {
          Mensagem: "Participe do programa Boa Hora: cuidado em cada etapa da gestação.",
          Link: "/programa-saude-2/boa-hora",
          Icone: "heart",
          Cor: "support-success",
          Action: "Ver detalhes",
          Alerta: "N"
        },
        ...messages
      ];
    },
    createToken(credentialId) {
      let cred = this.credentials[credentialId];
      if (cred.number === "" || !cred.number) return;
      const n = cred.number.split(" ");
      const secret = `${n[0]}${n[1]}`;
      return otp.getTokenOtp(secret);
    },
    irParaCredencial(credentialSelected) {
      this.$router.push({ path: `/credential/${credentialSelected}` });
    },
    onShowImagePicker() {
      this.showImageCropper = true;
    },
    hideImagePicker() {
      this.showImageCropper = false;
    },
    async onImageResult(imgUrl) {
      const blob = await fetch(imgUrl).then(res => res.blob());
      await this.changeProfilePick({ blobFile: blob });
      this.hideImagePicker();
      await this.fetchProfileImage();
    },
    async isCorrectHome() {
      const correctHome = this.defaultRoute;
      if (this.$route.path !== correctHome) {
        this.$router.replace(correctHome);
        return false;
      }
      return true;
    },
    closeModalMessage() {
      this.registerAlertNotification({
        chamadoReferenciado: this.modalMessage.Chamado,
        tipoAviso: this.modalMessage.TipoAviso,
        classificacao: this.modalMessage.Classificacao
      });
      this.modalMessage = {};
      this.displayModalMessage = false;
    },
    maybeOpenCheckupInvite() {
      if (this.displayCheckupInvite) return;
      if (!canSeeCheckupDigitalOmint(this.vinculo)) return;
      if (wasCheckupInviteDismissed()) return;
      this.$nextTick(() => {
        this.displayCheckupInvite = true;
      });
    },
    displayAlerts() {
      if (this.profile?.messages?.length) {
        const mensagem = this.profile.messages.find(m => m.Alerta === "X");
        if (mensagem) {
          this.modalMessage = { ...mensagem, MensagemPopUp: mensagem.MensagemPopUp.replaceAll("|", "<br />") };
          this.displayModalMessage = true;
        } else {
          this.modalMessage = { MensagemPopUp: "", Mensagem: "" };
          this.displayModalMessage = false;
        }
      }
      if (this.protocoloAvalicaoApp !== "") {
        Swal.fire({
          html: this.$refs.avaliacaoApp,
          showConfirmButton: false,
          showCloseButton: true
        }).then(_ => {
          this.CLEAR_PROTOCOLO_AVALIACAO_APP();
        });
      }

      const avisar = this.messagesToDisplay.filter(m => m.Alerta === "S");
      if (avisar && avisar.length > 0) {
        avisar.forEach(m => {
          Swal.fire({
            html: `<p style="text-align: left;">${m.MensagemPopUp.replaceAll("|", "<br />")}</p>`,
            reverseButtons: true,
            showCancelButton: true,
            confirmButtonText: "Ver Agora",
            cancelButtonText: "Ver Depois",
            allowEscapeKey: false,
            allowOutsideClick: false
          }).then(result => {
            try {
              this.registerAlertNotification({
                chamadoReferenciado: m.Chamado,
                tipoAviso: m.TipoAviso,
                classificacao: m.Classificacao
              });
            } finally {
              if (result.value) {
                if (m.Link.startsWith("/")) {
                  this.$router.push(m.Link);
                } else {
                  downloadUrl(m.Link);
                }
              }
            }
          });
        });
      }
    },
    quickMenuOnClick(link) {
      if (typeof link === "function") {
        link();
      } else if (link) {
        this.$router.push(link);
      }
    },
    isHubSaudeShortcut(item) {
      return isHubSaudeItem(item);
    },
    isBuscaRedeShortcut(item) {
      if (this.isHubSaudeShortcut(item)) return false;

      const view = String(item?.View || "")
        .toLowerCase()
        .replace(".html", "");
      const label = String(item?.Label || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim();

      // Evita "Busca de Rede IA"
      if (/\bia\b/.test(label) || view.includes("rede-ia") || view.includes("redeia") || view.includes("busca-ia")) {
        return false;
      }

      // Preferência: label exato "Busca de Rede"
      if (label === "busca de rede") return true;

      // Fallback pelo View da rede clássica (não IA)
      return (
        view.includes("redecredenciada-home") ||
        view === "/redecredenciada" ||
        view.endsWith("/redecredenciada") ||
        (view.includes("redecredenciada") && !view.includes("ia"))
      );
    }
  }
};
</script>

<style scoped>
.hide-option {
  display: none;
  opacity: 0;
  height: 0;
  width: 0;
  overflow: hidden;
}
.cropper {
  position: absolute;
  top: 65px;
  bottom: 0px;
  width: 100vw;
  background: rgba(0, 0, 0, 0.3);
  z-index: 99;
}

.shortcuts-section {
  background: var(--colors-light-primary-light-gray);
  overflow: visible;
}

.shortcuts-swiper-wrap {
  overflow: visible;
}

.AtalhosSwiper,
.AtalhosSwiper >>> .swiper,
.AtalhosSwiper >>> .swiper-wrapper,
.AtalhosSwiper >>> .swiper-slide {
  overflow: visible !important;
}

@media screen and (min-width: 1024px) {
  .shortcuts-section {
    background: white;
  }
}

.shortcut-item {
  position: relative;
  padding-top: 10px;
}

.shortcut-card {
  position: relative;
  overflow: visible !important;
}

.shortcut-card--hub {
  background: var(--colors-light-primary-navy-blue) !important;
  border-color: var(--colors-light-primary-navy-blue) !important;
}

.shortcut-new-badge {
  position: absolute;
  top: 0;
  left: 4px;
  background: var(--colors-light-support-news);
  color: #fff;
  border-radius: 16px;
  padding: 2px 8px;
  font-size: 10px;
  line-height: 14px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  z-index: 3;
  pointer-events: none;
  white-space: nowrap;
}
</style>

<style>
.shortcuts-swiper-wrap .swiper,
.shortcuts-swiper-wrap .swiper-wrapper,
.shortcuts-swiper-wrap .swiper-slide {
  overflow: visible !important;
}
</style>
